import { DatePipe, KeyValue } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  Signal,
  computed,
  inject
} from '@angular/core';
import { SelectComponent } from '@jeppesen-foreflight/jmkw-toolkit';
import { TwcTime } from '../../../twc-time';
import { TwcLayerInterface } from '../../../twc.mixin';
import { ColorService } from '../color.service';

@Component({
  selector: 'jmkw-time-span-select',
  templateUrl: './time-span-select.component.html',
  styleUrls: ['./time-span-select.component.scss'],
  standalone: true,
  imports: [SelectComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ColorService]
})
export class TimeSpanSelectComponent {
  datePipe = inject(DatePipe);
  colorService = inject(ColorService);
  @Input() layer?: TwcLayerInterface;
  @Input() timeMultiplier = 1;
  @Input() colors: string[] = [
    '#b8e3ff', // Light Blue
    '#a7ffeb', // Light Turquoise
    '#f8ffb8', // Light Yellow
    '#ffd1dc', // Light Pink
    '#d4ffd4', // Light Green
    '#f0f8ff' // Alice Blue
  ];
  @Output() selectionChanged = new EventEmitter<string>();

  timeOptions: Signal<
    Record<string, { id: string; twcTime: TwcTime; style: any }>
  > = computed(() => {
    const timeMap = this.layer?.timeMap?.();
    if (!timeMap) return {};

    const entries = [...timeMap.entries()];
    const maxTimeDifference = Math.max(
      ...entries.map(([, twcTime]) =>
        this.diffInMilliseconds(twcTime, this.timeMultiplier)
      )
    );

    return entries.reduce(
      (acc, [id, twcTime]) => {
        const key = this.twcTimeToKey(twcTime);
        const [color, backgroundColor] = this.calculateColors(
          twcTime,
          maxTimeDifference,
          this.colors,
          this.timeMultiplier
        );
        acc[key] = { id, twcTime, style: { backgroundColor, color } };
        return acc;
      },
      {} as Record<string, { id: string; twcTime: TwcTime; style: any }>
    );
  });

  calculateColors(
    twcTime: TwcTime,
    maxTimeDifference: number,
    colors: string[],
    timeMultiplier: number
  ): [string, string] {
    const timeDifference = Math.abs(
      this.diffInMilliseconds(twcTime, timeMultiplier)
    );
    const normalizedDifference = timeDifference / maxTimeDifference;
    const backgroundColor = this.colorService.calculateInterpolatedColor(
      colors,
      normalizedDifference
    );
    const color = 'rgba(0, 0, 0, 1)';
    return [color, backgroundColor];
  }

  diffInMilliseconds(twcTime: TwcTime, timeMultiplier = 1): number {
    if (twcTime.end || twcTime.start) {
      return Math.abs(
        Number(twcTime.end || twcTime.start) * timeMultiplier - Date.now()
      );
    }
    return 0;
  }

  get initialKey() {
    const selectedTime = this.selectedTimeId;
    return selectedTime
      ? this.twcTimeToKey(new TwcTime(selectedTime))
      : undefined;
  }

  get selectedTimeId(): string | undefined {
    const timeMap = this.layer?.timeMap?.();
    if (!timeMap?.size) return this.layer?.selectedTime;

    const firstTime = timeMap.keys().next().value;
    return this.layer?.selectedTime && timeMap.has(this.layer.selectedTime)
      ? this.layer.selectedTime
      : firstTime;
  }

  twcTimeToKey(twcTime: TwcTime) {
    return `${twcTime.stringify(this.timeMultiplier, 'dd/HH:mm')}z`;
  }

  onSelectionChangeHandler(
    keyValue: KeyValue<
      string,
      { id: string; twcTime: TwcTime; style: any }
    > | null
  ) {
    if (keyValue) {
      this.selectionChanged.emit(keyValue.value.id);
    }
  }

  compareFn(
    timeArrayA: KeyValue<string, { id: string; twcTime: TwcTime; style: any }>,
    timeArrayB: KeyValue<string, { id: string; twcTime: TwcTime; style: any }>
  ) {
    return timeArrayA.key === timeArrayB.key;
  }
}
